import { Directionality } from "@angular/cdk/bidi";
import { DOCUMENT } from "@angular/common";
import { Component, inject, OnDestroy, OnInit, Renderer2 } from "@angular/core";
import { RouterOutlet } from "@angular/router";
import { TranslocoService } from "@jsverse/transloco";
import { Subject, takeUntil } from "rxjs";
import { Constants } from "./core/constants/constants";
import { StorageUtilsService } from "./services/storage-utils/storage-utils.service";
import { Store } from "@ngrx/store";
import { setAppContext } from "./store/actions/context.actions";
import { AppContextModel } from "./shared/models/context.model";
import { AuthService } from "./core/auth/auth.service";
import { LanguagesService } from "./services/languages/languages.service";

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    imports: [RouterOutlet]
})
export class AppComponent implements OnDestroy, OnInit {
  document = inject(DOCUMENT);
  private _unsubscribeAll = new Subject<void>();
  private _storageUtilsService = inject(StorageUtilsService);
  private _store = inject(Store);
  private _authService = inject(AuthService);
  private directionality: Directionality = inject(Directionality);
  private translocoService: TranslocoService = inject(TranslocoService);
  private renderer: Renderer2 = inject(Renderer2);
  private _languagesService = inject(LanguagesService);

  /**
   * Constructor
   */
  constructor() {
    this.languagesChangeListener();
  }

  ngOnInit(): void {
    this.getCSRFToken();
    this.setAppContextAndLanguageValue();
  }
  /**
   * On component destroy, unsubscribe to avoid memory leaks
   */
  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  /** set app context & lang */
  setAppContextAndLanguageValue(): void {
    /** set app context */
    const appContextFromStorage =
      this._storageUtilsService.getAppContext() ??
      AppContextModel.globalContext;
    if (appContextFromStorage) {
      this._store.dispatch(
        setAppContext({ appContext: appContextFromStorage }),
      );
    }
  }

  /** language changes listener */
  languagesChangeListener(): void {
    this._languagesService.currentLanguage$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((activeLang) => {
        this.setDirectionRtlOrLtr(activeLang);
      });
  }

  /**
   * Change direction rtl <=> ltr based on active language
   * @param activeLanguage - The active language code
   */
  setDirectionRtlOrLtr(activeLanguage: string): void {
    const direction = activeLanguage === "dz" ? "rtl" : "ltr";

    (this.directionality as any).value = direction;

    this.renderer.setAttribute(this.document.documentElement, "dir", direction);

    this.updateBodyDirectionClass({ direction });
  }

  private updateBodyDirectionClass({
    direction,
  }: {
    direction: "rtl" | "ltr";
  }): void {
    const bodyClass =
      direction === "rtl"
        ? Constants.rtlDirectionClass
        : Constants.ltrDirectionClass;

    if (this.document.body.classList.contains(Constants.rtlDirectionClass)) {
      this.renderer.removeClass(
        this.document.body,
        Constants.rtlDirectionClass,
      );
    }

    if (this.document.body.classList.contains(Constants.ltrDirectionClass)) {
      this.renderer.removeClass(
        this.document.body,
        Constants.ltrDirectionClass,
      );
    }

    this.renderer.addClass(this.document.body, bodyClass);
  }

  /** get csrf token */
  getCSRFToken(): void {
    this._authService
      .getCSRFToken()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((res) => {
        this._storageUtilsService.setSmsVerification(res["smsVerification"]);
      });
  }
}
